import React from "react";
import { motion } from "framer-motion";
import { FaInstagram, FaFacebookF, FaLinkedinIn, FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";
import FooterLogo from "../asset/horizontal-3.png";

const SocialLink = ({ href, icon: Icon, label }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-12 h-12 rounded-full bg-purple900/50 backdrop-blur-sm
      flex items-center justify-center text-gold border border-gold/20
      hover:border-gold hover:text-gold hover:bg-purple900/80 
      transition-all duration-300 group"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    <Icon size={20} className="group-hover:scale-110 transition-transform duration-300" />
    <span className="sr-only">{label}</span>
  </motion.a>
);

export default function Footer() {
  return (
    <footer className="relative pt-20 pb-12 overflow-hidden">
      {/* Single Wave Animation */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute w-full -top-1" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <motion.path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill="#2d0036"
            initial={{ y: 0 }}
            animate={{ 
              y: [0, -20, 0],
              scaleY: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </svg>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-gold/20"
              style={{
                width: Math.random() * 4 + 2 + "px",
                height: Math.random() * 4 + 2 + "px",
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
              }}
              animate={{
                y: [0, -50, 0],
                x: [0, Math.random() * 30 - 15, 0],
                opacity: [0, 0.6, 0],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </div>

      {/* Content with enhanced styling */}
      <div className="relative container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-12 md:gap-8 mb-16">
          {/* Brand Column */}
          <div className="text-center md:text-left">
            <motion.img
              src={FooterLogo}
              alt="Kesari Glow"
              className="h-16 mx-auto md:mx-0 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            />
            <motion.p
              className="text-purple100/70 font-konnect"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Discover the timeless beauty secrets of Indian skincare tradition, 
              reimagined for the modern world.
            </motion.p>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-left">
            <motion.h3
              className="text-xl font-radon text-gold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Contact Info
            </motion.h3>
            <motion.div
              className="space-y-4 text-purple100/70 font-konnect"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <a 
                href="https://maps.app.goo.gl/pzF46qdeWjsGkgpA8"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center md:justify-start gap-3 hover:text-gold transition-colors duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-purple900/50 flex items-center justify-center border border-gold/20 group-hover:border-gold/50">
                  <FaMapMarkerAlt className="text-gold/70" />
                </div>
                <div className="text-left">
                  <p>G-17, SHIV PLAZA, Gadhpur Rd,</p>
                  <p>near SHYAMDHAM MANDIR,</p>
                  <p>Surat, Gujarat 395010</p>
                </div>
              </a>

              <a 
                href="mailto:support@kesariglow.in"
                className="flex items-center justify-center md:justify-start gap-3 hover:text-gold transition-colors duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-purple900/50 flex items-center justify-center border border-gold/20 group-hover:border-gold/50">
                  <FaEnvelope className="text-gold/70" />
                </div>
                <span>support@kesariglow.in</span>
              </a>

              <a 
                href="tel:+919898846514"
                className="flex items-center justify-center md:justify-start gap-3 hover:text-gold transition-colors duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-purple900/50 flex items-center justify-center border border-gold/20 group-hover:border-gold/50">
                  <FaPhone className="text-gold/70" />
                </div>
                <span>+91 98988 46514</span>
              </a>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <motion.h3
              className="text-xl font-radon text-gold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Quick Links
            </motion.h3>
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {['Home', 'Products', 'About', 'Contact'].map((link) => (
                <a
                  key={link}
                  href={`#${link.toLowerCase()}`}
                  className="block text-purple100/70 hover:text-gold transition-colors duration-300"
                >
                  {link}
                </a>
              ))}
            </motion.div>
          </div>

          {/* Social Links */}
          <div className="text-center md:text-right">
            <motion.h3
              className="text-xl font-radon text-gold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Connect With Us
            </motion.h3>
            <motion.div
              className="flex justify-center md:justify-end gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <SocialLink 
                href="https://www.instagram.com/kesariglowofficial?igsh=YjY4YjZicjcyMW9j" 
                icon={FaInstagram} 
                label="Instagram" 
              />
              <SocialLink 
                href="https://www.facebook.com/share/14DYDJxdryD/" 
                icon={FaFacebookF} 
                label="Facebook" 
              />
              <SocialLink 
                href="https://www.linkedin.com/company/kesariglow/" 
                icon={FaLinkedinIn} 
                label="LinkedIn" 
              />
            </motion.div>
          </div>
        </div>

        {/* Enhanced Divider */}
        <motion.div
          className="relative h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent my-12"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold/20" />
        </motion.div>

        {/* Copyright with enhanced styling */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-purple100/50 font-konnect text-sm">
            © {new Date().getFullYear()} Kesari Glow. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
} 